import { MutationOptions } from '@apollo/client';

import { useCurrentUser } from '@/hooks';
import { buildCurrentDateTime } from '@/services/graphql/hooks/mutations/helpers/helper';

import {
  ResetPersonConsentMutation,
  ResetPersonConsentMutationVariables,
  useResetPersonConsentMutation as useResetPersonConsentMutationApollo,
} from '../generated';

type ResetPersonConsentVariables =
  // oid is required
  Pick<ResetPersonConsentMutationVariables, 'oid'> &
    // issuerPersonId and actionAt are optional
    Partial<
      Pick<ResetPersonConsentMutationVariables, 'issuerPersonId' | 'actionAt'>
    >;

/**
 * __useResetPersonConsentMutation__
 *
 * To run a mutation, you first call `useResetPersonConsentMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useResetPersonConsentMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [resetPersonConsent, { data, loading, error }] = useResetPersonConsentMutation();
 *
 * resetPersonConsent({
 *   variables: {
 *      oid: // defaults to a new uuid
 *
 *      // Optional
 *      issuerPersonId: // defaults to the current user
 *      actionAt: // defaults to the current timestamp
 *   },
 * });
 */
export const useResetPersonConsentMutation = (
  baseOptions?: MutationOptions<
    ResetPersonConsentMutation,
    ResetPersonConsentMutationVariables
  >
) => {
  const { currentUser } = useCurrentUser();

  const [resetPersonConsent, status] = useResetPersonConsentMutationApollo({
    // We invalidate all queries related to consent to refresh the data
    refetchQueries: ['getPersonConsentList', 'getPersonCommunicationData'],

    ...baseOptions,
  });

  const doMutate = async (options: {
    variables?: ResetPersonConsentVariables;
    onCompleted?: (data: any) => void;
    onError?: (error: any) => void;
  }) => {
    const defaultVariables = {
      issuerPersonId: currentUser?.person?.oid ?? '',
      actionAt: buildCurrentDateTime(),

      // placeholders, have to be overwritten when calling the mutate function
      oid: '',
    };

    return resetPersonConsent({
      ...options,
      variables: { ...defaultVariables, ...(options?.variables ?? {}) },
    });
  };

  return [doMutate, status] as const;
};
